import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  deleteSavedSearch,
  listSavedSearches,
  setDefaultSavedSearch,
  updateSavedSearch,
  markSavedSearchUsed,
} from "../../api/savedSearchesApi";
import StatusBadge from "../../components/midpoint/intelligence/StatusBadge";
import { formatDateTime } from "../../components/midpoint/admin/users/adminUserFormatters";
import { storeSavedSearchRestore } from "../../utils/savedSearchState";

const FREQUENCY_OPTIONS = [
  { value: "", label: "Account default" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "never", label: "Never" },
];

const SavedSearchesPage = () => {
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [editingId, setEditingId] = useState("");
  const [editName, setEditName] = useState("");
  const [editNotificationEnabled, setEditNotificationEnabled] = useState(false);
  const [editNotificationFrequency, setEditNotificationFrequency] = useState("");

  const loadSearches = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const body = await listSavedSearches({ pageSize: 100 });
      setSearches(body.data || []);
    } catch (error) {
      setSearches([]);
      setErrorMessage(error.message || "Unable to load saved searches.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSearches();
  }, [loadSearches]);

  const filtered = searches.filter((row) =>
    row.name.toLowerCase().includes(searchFilter.trim().toLowerCase())
  );

  const handleRun = async (savedSearch) => {
    try {
      const body = await markSavedSearchUsed(savedSearch.savedSearchId);
      storeSavedSearchRestore(body.savedSearch);
      navigate("/");
    } catch (error) {
      setErrorMessage(error.message || "Unable to run saved search.");
    }
  };

  const handleDelete = async (savedSearchId) => {
    if (!window.confirm("Delete this saved search?")) {
      return;
    }

    try {
      await deleteSavedSearch(savedSearchId);
      await loadSearches();
    } catch (error) {
      setErrorMessage(error.message || "Unable to delete saved search.");
    }
  };

  const handleSetDefault = async (savedSearchId) => {
    try {
      await setDefaultSavedSearch(savedSearchId);
      await loadSearches();
    } catch (error) {
      setErrorMessage(error.message || "Unable to set default.");
    }
  };

  const handleSaveRename = async (savedSearchId) => {
    try {
      await updateSavedSearch(savedSearchId, { name: editName.trim() });
      setEditingId("");
      await loadSearches();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update saved search.");
    }
  };

  const handleNotificationToggle = async (row, enabled) => {
    try {
      await updateSavedSearch(row.savedSearchId, { notificationEnabled: enabled });
      setSuccessMessage("Alert settings updated.");
      await loadSearches();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update alert settings.");
    }
  };

  const handleFrequencyChange = async (row, frequency) => {
    try {
      await updateSavedSearch(row.savedSearchId, {
        notificationFrequency: frequency || null,
      });
      setSuccessMessage("Alert frequency updated.");
      await loadSearches();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update alert frequency.");
    }
  };

  const openEditModal = (row) => {
    setEditingId(row.savedSearchId);
    setEditName(row.name);
    setEditNotificationEnabled(Boolean(row.notificationEnabled));
    setEditNotificationFrequency(row.notificationFrequency || "");
  };

  const handleSaveEdit = async (savedSearchId) => {
    try {
      await updateSavedSearch(savedSearchId, {
        name: editName.trim(),
        notificationEnabled: editNotificationEnabled,
        notificationFrequency: editNotificationFrequency || null,
      });
      setEditingId("");
      setSuccessMessage("Saved search updated.");
      await loadSearches();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update saved search.");
    }
  };

  return (
    <section className="panel-card">
      <div className="account-security-section__header">
        <h2>Saved searches</h2>
        <Link to="/" className="midpoint-share-button">
          Browse map
        </Link>
      </div>

      <p className="muted">
        Enable email alerts per search or set defaults on{" "}
        <Link to="/account/notifications">Notifications</Link>.
      </p>

      <input
        type="search"
        placeholder="Filter saved searches"
        value={searchFilter}
        onChange={(event) => setSearchFilter(event.target.value)}
      />

      {errorMessage ? <p className="admin-data-health-error">{errorMessage}</p> : null}
      {successMessage ? <p className="admin-users-success">{successMessage}</p> : null}
      {isLoading ? <p className="muted">Loading…</p> : null}

      {!isLoading && filtered.length ? (
        <table className="admin-data-health-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Mode</th>
              <th>Alerts</th>
              <th>Frequency</th>
              <th>Last used</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.savedSearchId}>
                <td>
                  {row.name}
                  {row.isDefault ? <StatusBadge label="Default" tone="confirmed" /> : null}
                </td>
                <td>{row.listingMode || "any"}</td>
                <td>
                  <label className="admin-users-checkbox">
                    <input
                      type="checkbox"
                      checked={Boolean(row.notificationEnabled)}
                      onChange={(event) =>
                        handleNotificationToggle(row, event.target.checked)
                      }
                    />
                    {row.notificationEnabled ? "On" : "Off"}
                  </label>
                </td>
                <td>
                  <select
                    value={row.notificationFrequency || ""}
                    onChange={(event) => handleFrequencyChange(row, event.target.value)}
                    disabled={!row.notificationEnabled}
                  >
                    {FREQUENCY_OPTIONS.map((option) => (
                      <option key={option.value || "default"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>{formatDateTime(row.lastUsedAt || row.updatedAt)}</td>
                <td>
                  <button
                    type="button"
                    className="midpoint-share-button"
                    onClick={() => handleRun(row)}
                  >
                    Run
                  </button>
                  <button
                    type="button"
                    className="midpoint-share-button"
                    onClick={() => openEditModal(row)}
                  >
                    Edit
                  </button>
                  {!row.isDefault ? (
                    <button
                      type="button"
                      className="midpoint-share-button"
                      onClick={() => handleSetDefault(row.savedSearchId)}
                    >
                      Set default
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="midpoint-share-button"
                    onClick={() => handleDelete(row.savedSearchId)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {!isLoading && !filtered.length ? (
        <p className="muted">No saved searches yet. Use Save Search on the map.</p>
      ) : null}

      {editingId ? (
        <div className="admin-re-modal-backdrop" role="presentation">
          <div className="admin-re-modal panel-card" role="dialog" aria-labelledby="edit-search-title">
            <h2 id="edit-search-title">Edit saved search</h2>
            <form
              className="admin-users-form"
              onSubmit={(event) => {
                event.preventDefault();
                handleSaveEdit(editingId);
              }}
            >
              <label className="admin-users-field">
                <span>Name</span>
                <input
                  type="text"
                  value={editName}
                  onChange={(event) => setEditName(event.target.value)}
                  required
                />
              </label>
              <label className="admin-users-checkbox">
                <input
                  type="checkbox"
                  checked={editNotificationEnabled}
                  onChange={(event) => setEditNotificationEnabled(event.target.checked)}
                />
                Email alerts enabled
              </label>
              <label className="admin-users-field">
                <span>Alert frequency</span>
                <select
                  value={editNotificationFrequency}
                  onChange={(event) => setEditNotificationFrequency(event.target.value)}
                  disabled={!editNotificationEnabled}
                >
                  {FREQUENCY_OPTIONS.map((option) => (
                    <option key={option.value || "default"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="admin-users-form__actions">
                <button type="submit" className="report-submit-cta">
                  Save
                </button>
                <button
                  type="button"
                  className="midpoint-share-button"
                  onClick={() => setEditingId("")}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default SavedSearchesPage;
